import { twMerge } from "tailwind-merge";
import { PageContainer } from "../../../components/pageContainer/PageContainer";
import { Button, Card, H5, H6 } from "@sellar-io/components";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import {
  calculateItemTotal,
  calculateVatTotal,
  formatCurrency,
  isCourierIntegrationFulfillmentType,
} from "@sellar-io/common";
import { useState } from "react";
import { useNavigate } from "react-router";
import { SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { CommentDialog } from "../../../components/comments/CommentDialog";
import { ErrorMessage } from "../../../components/errorMessage/ErrorMessage";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { ORDER_DETAILS, ORDER_EDIT } from "../../../constants/routes";
import { useBasketContext, useEvents } from "../../../hooks";
import { useOrder } from "../../../hooks/api/v2/order/useOrder";
import { useOrderCommentCreate } from "../../../hooks/api/v2/orderComment/useOrderCommentCreate";
import { useSupplierRelationshipList } from "../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipList";
import { useFeatureFlagStore } from "../../../hooks/useFeatureFlags";
import { useParamAsNumber } from "../../../hooks/useParamAsNumber";
import { Mixpanel } from "../../../utils/mixpanel";
import { generateAddressString } from "../../../utils/sellarHelpers";
import { CancelRecreateOrderDialogActive } from "../modals/CheckoutDialogs/CancelRecreateOrderDialog";
import { OrderEditItem } from "./OrderEditItem";
import { OrderEditItemAdd } from "./OrderEditItemAdd";
import {
  OrderEditSummaryRow,
  OrderEditSummaryRowDelivery,
  OrderEditSummaryRowDiscount,
} from "./OrderEditSummaryRow";
import { useStockListOrderSummaryProps } from "../../stockList/useStockListOrderSummaryProps";
import { useSupplierStorefrontV2 } from "../../../hooks/api/v2/supplier/useSupplierStorefrontV2";

export const OrderEdit = () => {
  const { track } = useEvents();
  const navigate = useNavigate();

  const orderId = useParamAsNumber(ORDER_EDIT, "orderId");

  if (!orderId) throw new Error("Cannot determine orderId");

  const [{ temporaryCartId, connection }] = useBasketContext();

  const orderQuery = useOrder(orderId);

  const updateOrderTracking = async () => {
    const updatedOrder = await orderQuery.refetch();
    if (updatedOrder.data) {
      const trackingOrderTotal = Mixpanel.formatAmountForTracking(
        updatedOrder.data.price,
      );
      const trackingTransactionFee = Mixpanel.formatAmountForTracking(
        updatedOrder.data.transactionFee,
      );

      return track("Order Updated", {
        orderId: updatedOrder.data.id,
        temporaryCartId,
        total: trackingOrderTotal,
        vatPercentage: updatedOrder.data.vatPercentage,
        transactionFee: trackingTransactionFee,
        paymentType: updatedOrder.data.paymentType,
        currency: updatedOrder.data.currency,
        fulfillmentType: updatedOrder.data.fulfillmentType,
        initiator: updatedOrder.data.initiator,
        productsOrderedCount: updatedOrder.data.OrderItems.length,
        discountAmount: updatedOrder.data.discountAmount,
        hasSupplierRetailerConnection: !!connection?.approved,
      });
    }
  };

  return (
    <>
      <NavigationBar
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={() => navigate(-1)}
        title={"Edit order items"}
      />
      {orderQuery.isLoading ? (
        <SpinnerLegacy />
      ) : orderQuery.isError ? (
        <ErrorMessage>There was an error.</ErrorMessage>
      ) : (
        orderQuery.data && (
          <OrderEditContent
            order={orderQuery.data}
            updateOrderTracking={updateOrderTracking}
          />
        )
      )}
    </>
  );
};

const OrderEditContent = ({
  order,
  updateOrderTracking,
}: {
  order: OrderGetResponseType;
  updateOrderTracking: () => void;
}) => {
  const navigate = useNavigate();
  const orderCommentCreate = useOrderCommentCreate();
  const [showCheckoutIncreaseAlert, setShowCheckoutIncreaseAlert] =
    useState<boolean>(false);
  const { isActive } = useFeatureFlagStore();

  if (!isActive("ORDER_PARTIAL_REFUNDS") && order.paymentType == "checkout")
    throw new Error("Editing is not available on this order.");

  const supplierRelationshipQuery = useSupplierRelationshipList(
    {
      query: [
        {
          type: "buyerId",
          modifier: "EQUALS",
          value: order.RetailerId,
        },
      ],
      limit: 1,
    },

    {
      enabled: !!order.RetailerId,
    },
  );

  const connection = supplierRelationshipQuery.data?.pages[0]?.[0];
  const [addCommentActive, setAddCommentActive] = useState(false);

  const fulfillmentType = () => {
    if (isCourierIntegrationFulfillmentType(order.fulfillmentType))
      return "courier";
    return order.fulfillmentType;
  };

  const supplierStorefrontQuery = useSupplierStorefrontV2(order.SupplierId, {
    fulfillmentType: fulfillmentType(),
    selectedPriceTierId: order.PriceTierId ?? undefined,
    selectedAvailabilityTierId: order.AvailabilityTierId ?? undefined,
  });

  const supplierStorefront = supplierStorefrontQuery.data;

  const priceTierDiscounts =
    supplierStorefront?.VolumeDiscounts?.PriceTierDiscounts ?? [];

  const priceTierOrderRules =
    supplierStorefront?.FulfillmentType.OrderRules?.PriceTierOrderRules ??
    supplierStorefront?.OrderRules?.PriceTierOrderRules ??
    [];

  const minimumOrderPrice =
    supplierStorefront?.FulfillmentType.OrderRules?.minimumOrderPrice ??
    supplierStorefront?.OrderRules?.minimumOrderPrice ??
    0;

  const freeDeliveryBasketSubtotal =
    supplierStorefront?.FulfillmentType.freeDeliveryBasketSubtotal ?? null;

  const {
    apiValues: { deliveryCharge, discountAmount },
  } = useStockListOrderSummaryProps({
    order,
    deliveryChargeRules:
      supplierStorefront?.FulfillmentType.DeliveryChargeRules,
    deliveryCharge: supplierStorefront?.FulfillmentType.charge,
    deliveryChargeCap: supplierStorefront?.FulfillmentType.deliveryChargeCap,
    fulfillmentTypeContainerDeliveryCharges:
      supplierStorefront?.FulfillmentType
        .FulfillmentTypeContainerDeliveryCharges,
    priceTierDiscounts,
    caseSizeRuleList: supplierStorefront?.CaseSizeRules.list ?? [],
    priceTierOrderRules,
    minimumOrderPrice,
    containerTypeDeliveryChargeRules:
      supplierStorefront?.FulfillmentType.ContainerTypeDeliveryChargeRules ??
      [],
    freeDeliveryBasketSubtotal,
  });

  return (
    <PageContainer>
      {connection?.id && (
        <OrderEditContentAlerts
          showCheckoutIncreaseAlert={showCheckoutIncreaseAlert}
          order={order}
          connectionId={connection.id}
        />
      )}
      <Card className={twMerge("bg-white", "p-4")}>
        <H5>{order.Retailer.Business.name}</H5>
        <H6>{generateAddressString(order.DeliveryAddress)}</H6>
      </Card>
      <Card className={twMerge("bg-white", "flex flex-col gap-4 p-4")}>
        <H5>Order #{order.id}</H5>
        <div className={"flex flex-col gap-4"}>
          {order.OrderItems.map((item) => (
            <OrderEditItem
              key={item.id}
              orderItem={item}
              currency={order.currency}
              onDelete={updateOrderTracking}
              onUpdate={updateOrderTracking}
              setShowCheckoutIncreaseAlert={setShowCheckoutIncreaseAlert}
            />
          ))}
        </div>
        <OrderEditItemAdd
          onSuccess={updateOrderTracking}
          retailerId={order.RetailerId}
        />
      </Card>
      <Card
        className={twMerge(
          "bg-white",
          "grid grid-cols-[1fr_100px_auto] gap-4 p-4",
        )}
      >
        <OrderEditSummaryRow
          name={"Subtotal"}
          value={formatCurrency(calculateItemTotal(order.OrderItems))}
        />

        {supplierStorefrontQuery.isFetched && (
          <>
            <OrderEditSummaryRowDelivery
              orderId={order.id}
              deliveryCharge={order.deliveryCharge}
              calculatedDeliveryCharge={deliveryCharge}
              onSuccess={updateOrderTracking}
              setShowCheckoutIncreaseAlert={setShowCheckoutIncreaseAlert}
              order={order}
            />

            <OrderEditSummaryRowDiscount
              orderId={order.id}
              discountAmount={order.discountAmount}
              calculatedDiscountAmount={discountAmount}
              onSuccess={updateOrderTracking}
              order={order}
              setShowCheckoutIncreaseAlert={setShowCheckoutIncreaseAlert}
            />
          </>
        )}

        <OrderEditSummaryRow
          name={`VAT (${order.vatPercentage}%)`}
          value={formatCurrency(
            calculateVatTotal({ ...order, orderItems: order.OrderItems }),
            order.currency,
          )}
        />

        <H5>Order Total</H5>
        <H5 className={"text-right"}>{formatCurrency(order.price)}</H5>
        <div />
      </Card>
      <Button
        className={"w-full"}
        onClick={() =>
          connection?.connectionType === "customer"
            ? setAddCommentActive(true)
            : navigate(ORDER_DETAILS.href({ orderId: order.id }))
        }
      >
        Done
      </Button>
      <CommentDialog
        orderId={order.id}
        active={addCommentActive}
        onDismiss={() => setAddCommentActive(false)}
        type={"comment"}
        mutation={orderCommentCreate}
        onSuccess={() => navigate(ORDER_DETAILS.href({ orderId: order.id }))}
      />
    </PageContainer>
  );
};

const OrderEditContentAlerts = ({
  order,
  showCheckoutIncreaseAlert,
  connectionId,
}: {
  order: OrderGetResponseType;
  showCheckoutIncreaseAlert: boolean;
  connectionId: number;
}) => {
  const [cancelRecreateDialogActive, setCancelRecreateDialogActive] =
    useState<boolean>(false);

  const orderAmountDecreased = order.paidAmount > order.price;
  return (
    <>
      {showCheckoutIncreaseAlert && (
        <Alert
          message={`You will not be able to save these changes. Make the order total lower than the total already paid by ${order.Retailer.Business.name}, or cancel & recreate the order.`}
          title={"Order total increased"}
          type={"warning"}
        >
          <Button
            color={"secondary"}
            className={"border-orange-300 bg-orange-100 text-orange-800"}
            onClick={() => {
              setCancelRecreateDialogActive(true);
            }}
          >
            Cancel & re-create order
          </Button>
        </Alert>
      )}

      {orderAmountDecreased && !showCheckoutIncreaseAlert && (
        <Alert
          message={`Add a comment to let ${order.Retailer.Business.name} know why you've made changes to their order.`}
          type={"information"}
          title={"Order total decreased"}
          className={"w-full"}
        />
      )}
      {connectionId && (
        <CancelRecreateOrderDialogActive
          active={cancelRecreateDialogActive}
          onDismiss={() => setCancelRecreateDialogActive(false)}
          order={order}
          connectionId={connectionId}
        />
      )}
    </>
  );
};
